import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  TextField,
  IconButton,
  useTheme,
  Hidden,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import { Autocomplete } from "@material-ui/lab";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import SwitchCommon from "../../../../../../components/switches/SwitchCommon";
import { CustomTheme } from "../../../../../../types/customTheme";

interface ProductListItemProps {
  product: any;
  index: number;
  isExpanded: boolean;
  isLastItem: boolean;
  onExpandToggle: (id: string | number) => void;
  stockDepartmentSelectedNode: any[];
  selectedDepartmentObj: any;
  onDepartmentChange: (dept: any, productId: string) => void;
  classes: any;
  calculateMargin: (price: number, cost: number) => string;
  onFieldChange: (field: string, value: any, productId: string) => void;
  onImmediateValidToggle?: (
    productId: string,
    checked: boolean,
    product: any,
  ) => void;
  isEditDisabled?: boolean;
}

const ProductListItem: React.FC<ProductListItemProps> = ({
  product,
  index,
  isExpanded,
  isLastItem,
  onExpandToggle,
  stockDepartmentSelectedNode,
  selectedDepartmentObj,
  onDepartmentChange,
  classes,
  calculateMargin,
  onFieldChange,
  onImmediateValidToggle,
  isEditDisabled = false,
}) => {
  // ✅ All Hooks must be called before any early return
  const theme: CustomTheme = useTheme();

  const [localPrice, setLocalPrice] = useState<string>(
    product.price !== undefined && product.price !== null
      ? product.price.toString()
      : "0",
  );
  const [localCost, setLocalCost] = useState<string>(
    product.cost !== undefined && product.cost !== null
      ? product.cost.toString()
      : "0",
  );
  const [localDesc, setLocalDesc] = useState(product.desc || "");
  const [localSize, setLocalSize] = useState(product.size || "Regular");
  const [localPrintName, setLocalPrintName] = useState(
    product.printName || product.name,
  );
  const [localInTax, setLocalInTax] = useState(
    product.inTax ? `${product.inTax}%` : "20%",
  );
  const [localOutTax, setLocalOutTax] = useState(
    product.outTax ? `${product.outTax}%` : "20%",
  );
  const [isValidProduct, setIsValidProduct] = useState(
    product.valid !== undefined ? product.valid : true,
  );

  useEffect(() => {
    setIsValidProduct(product.valid !== undefined ? product.valid : true);
    setLocalDesc(product.desc || "");
    setLocalSize(product.size || "Regular");
    setLocalPrintName(product.printName || product.name);
    setLocalPrice(
      product.price !== undefined && product.price !== null
        ? product.price.toString()
        : "0",
    );
    setLocalCost(
      product.cost !== undefined && product.cost !== null
        ? product.cost.toString()
        : "0",
    );
    setLocalInTax(product.inTax ? `${product.inTax}%` : "20%");
    setLocalOutTax(product.outTax ? `${product.outTax}%` : "20%");
  }, [product]);

  // ✅ Early return after all Hooks are called
  // Skip rendering if product has no name
  if (!product || !product.name || product.name.trim() === "") {
    return null;
  }

  const handleValidToggle = (checked: boolean) => {
    setIsValidProduct(checked);
    if (onImmediateValidToggle) {
      onImmediateValidToggle(product.id, checked, product);
    }
  };

  const safeParseNumber = (value: string): number => {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditDisabled) return;
    const rawValue = e.target.value;
    const numericValue = rawValue.replace(/[^0-9.-]/g, "");
    setLocalPrice(numericValue);

    const numValue = safeParseNumber(numericValue);
    if (onFieldChange && !isNaN(numValue)) {
      onFieldChange("price", numValue, product.id);
    }
  };

  const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditDisabled) return;
    const rawValue = e.target.value;
    const numericValue = rawValue.replace(/[^0-9.-]/g, "");
    setLocalCost(numericValue);

    const numValue = safeParseNumber(numericValue);
    if (onFieldChange && !isNaN(numValue)) {
      onFieldChange("cost", numValue, product.id);
    }
  };

  return (
    <Box
      style={{
        border: "none",
        backgroundColor: theme.palette.background.entity_background,
        overflow: "hidden",
        borderTopLeftRadius: index === 0 ? "10px" : "0px",
        borderTopRightRadius: index === 0 ? "10px" : "0px",
        borderBottomLeftRadius: isLastItem ? "10px" : "0px",
        borderBottomRightRadius: isLastItem ? "10px" : "0px",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        p={2}
        style={{ cursor: isEditDisabled ? "default" : "pointer" }}
        onClick={() => {
          if (!isEditDisabled) {
            onExpandToggle(product.id || index);
          }
        }}
      >
        <Hidden xsDown>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={1}>
              <Typography style={{ fontWeight: "bold" }} align="left">
                {index + 1}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                style={{
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                align="left"
              >
                {product.name}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography align="left">{localSize}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography style={{ fontSize: "0.85rem" }} align="left">
                PLU: {product.plu}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                style={{ gap: "16px" }}
              >
                <Box onClick={(e) => e.stopPropagation()}>
                  <FormControlLabel
                    control={<SwitchCommon />}
                    checked={isValidProduct}
                    onChange={(e: any) => handleValidToggle(e.target.checked)}
                    label=""
                    disabled={isEditDisabled}
                  />
                </Box>
                <Typography variant="body2">Valid</Typography>
                <IconButton
                  size="small"
                  style={{
                    transition: "0.3s",
                    transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
                  }}
                  disabled={isEditDisabled}
                >
                  <Icon icon="lucide:chevron-up" />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Hidden>

        <Hidden smUp>
          <Box width="100%">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography style={{ fontWeight: "bold" }}>
                {index + 1}. {product.name}
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                <Box onClick={(e) => e.stopPropagation()}>
                  <FormControlLabel
                    control={<SwitchCommon />}
                    checked={isValidProduct}
                    onChange={(e: any) => handleValidToggle(e.target.checked)}
                    label=""
                    disabled={isEditDisabled}
                  />
                </Box>
                <Typography variant="body2">Valid</Typography>
                <IconButton
                  size="small"
                  style={{
                    transition: "0.3s",
                    transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
                  }}
                  disabled={isEditDisabled}
                >
                  <Icon icon="lucide:chevron-up" />
                </IconButton>
              </Box>
            </Box>
            <Box display="flex" style={{ gap: "16px" }}>
              <Typography variant="body2" style={{ fontSize: "0.85rem" }}>
                Size: {localSize}
              </Typography>
              <Typography variant="body2" style={{ fontSize: "0.85rem" }}>
                PLU: {product.plu}
              </Typography>
            </Box>
          </Box>
        </Hidden>
      </Box>

      {isExpanded && (
        <>
          <Divider style={{ backgroundColor: "rgba(255,255,255,0.05)" }} />
          <Box
            p={3}
            style={{ backgroundColor: theme.palette.background.default }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="caption"
                  style={{
                    display: "block",
                    marginBottom: 8,
                  }}
                  align="left"
                >
                  Description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  variant="outlined"
                  className={classes.textField}
                  value={localDesc}
                  onChange={(e) => {
                    if (!isEditDisabled) {
                      setLocalDesc(e.target.value);
                      onFieldChange("desc", e.target.value, product.id);
                    }
                  }}
                  disabled={isEditDisabled}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                      align="left"
                    >
                      Size
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      value={localSize}
                      onChange={(e) => {
                        if (!isEditDisabled) {
                          setLocalSize(e.target.value);
                          onFieldChange("size", e.target.value, product.id);
                        }
                      }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                      align="left"
                    >
                      Department{" "}
                      <span style={{ color: theme.palette.custom.red.main }}>
                        *
                      </span>
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={stockDepartmentSelectedNode}
                      getOptionLabel={(o: any) => o.name || ""}
                      classes={{ root: classes.autoComplete }}
                      renderInput={(p) => (
                        <TextField {...p} variant="outlined" />
                      )}
                      value={selectedDepartmentObj}
                      onChange={(e, value) => {
                        if (!isEditDisabled) {
                          onDepartmentChange(value, product.id);
                          if (onFieldChange) {
                            onFieldChange(
                              "departmentId",
                              value?.id,
                              product.id,
                            );
                          }
                        }
                      }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                      align="left"
                    >
                      Print Name
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      value={localPrintName}
                      onChange={(e) => {
                        if (!isEditDisabled) {
                          setLocalPrintName(e.target.value);
                          onFieldChange(
                            "printName",
                            e.target.value,
                            product.id,
                          );
                        }
                      }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                      align="left"
                    >
                      Global Product{" "}
                      <span style={{ color: theme.palette.custom.red.main }}>
                        *
                      </span>
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={[]}
                      classes={{ root: classes.autoComplete }}
                      renderInput={(p) => (
                        <TextField {...p} variant="outlined" />
                      )}
                      defaultValue={{ label: product.name }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                      align="left"
                    >
                      Classification{" "}
                      <span style={{ color: theme.palette.custom.red.main }}>
                        *
                      </span>
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={[
                        { label: "Main Course" },
                        { label: "Appetizer" },
                        { label: "Dessert" },
                        { label: "Beverage" },
                      ]}
                      getOptionLabel={(o: any) => o.label}
                      classes={{ root: classes.autoComplete }}
                      renderInput={(p) => (
                        <TextField {...p} variant="outlined" />
                      )}
                      defaultValue={{ label: "Main Course" }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Hidden xsDown>
              <Box mt={3} display="flex" style={{ gap: "16px" }}>
                <Box style={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginBottom: 4,
                    }}
                    align="left"
                  >
                    Price{" "}
                    <span style={{ color: theme.palette.custom.red.main }}>
                      *
                    </span>
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.textField}
                    value={localPrice}
                    onChange={handlePriceChange}
                    placeholder="0.00"
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 4 }}>£</span>,
                    }}
                    disabled={isEditDisabled}
                  />
                </Box>

                <Box style={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginBottom: 4,
                    }}
                    align="left"
                  >
                    Cost
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.textField}
                    value={localCost}
                    onChange={handleCostChange}
                    placeholder="0.00"
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 4 }}>£</span>,
                    }}
                    disabled={isEditDisabled}
                  />
                </Box>

                <Box style={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginBottom: 4,
                    }}
                    align="left"
                  >
                    Margin
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.textField}
                    disabled={true}
                    value={calculateMargin(
                      safeParseNumber(localPrice),
                      safeParseNumber(localCost),
                    )}
                  />
                </Box>

                <Box style={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginBottom: 4,
                    }}
                    align="left"
                  >
                    In Tax{" "}
                    <span style={{ color: theme.palette.custom.red.main }}>
                      *
                    </span>
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={["20%", "5%", "0%"]}
                    classes={{ root: classes.autoComplete }}
                    renderInput={(p) => <TextField {...p} variant="outlined" />}
                    value={localInTax}
                    onChange={(e, value) => {
                      if (!isEditDisabled && value !== null) {
                        setLocalInTax(value);
                        const taxValue = parseInt(value.replace("%", ""));
                        if (onFieldChange && !isNaN(taxValue)) {
                          onFieldChange("inTax", taxValue, product.id);
                        }
                      }
                    }}
                    disabled={isEditDisabled}
                  />
                </Box>

                <Box style={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginBottom: 4,
                    }}
                    align="left"
                  >
                    Out Tax{" "}
                    <span style={{ color: theme.palette.custom.red.main }}>
                      *
                    </span>
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={["20%", "5%", "0%"]}
                    classes={{ root: classes.autoComplete }}
                    renderInput={(p) => <TextField {...p} variant="outlined" />}
                    value={localOutTax}
                    onChange={(e, value) => {
                      if (!isEditDisabled && value !== null) {
                        setLocalOutTax(value);
                        const taxValue = parseInt(value.replace("%", ""));
                        if (onFieldChange && !isNaN(taxValue)) {
                          onFieldChange("outTax", taxValue, product.id);
                        }
                      }
                    }}
                    disabled={isEditDisabled}
                  />
                </Box>
              </Box>
            </Hidden>

            <Hidden smUp>
              <Box mt={3}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Price{" "}
                      <span style={{ color: theme.palette.custom.red.main }}>
                        *
                      </span>
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      value={localPrice}
                      onChange={handlePriceChange}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <span style={{ marginRight: 4 }}>£</span>
                        ),
                      }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Cost
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      value={localCost}
                      onChange={handleCostChange}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <span style={{ marginRight: 4 }}>£</span>
                        ),
                      }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Margin
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      disabled={true}
                      value={calculateMargin(
                        safeParseNumber(localPrice),
                        safeParseNumber(localCost),
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      In Tax{" "}
                      <span style={{ color: theme.palette.custom.red.main }}>
                        *
                      </span>
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={["20%", "5%", "0%"]}
                      classes={{ root: classes.autoComplete }}
                      renderInput={(p) => (
                        <TextField {...p} variant="outlined" />
                      )}
                      value={localInTax}
                      onChange={(e, value) => {
                        if (!isEditDisabled && value !== null) {
                          setLocalInTax(value);
                          const taxValue = parseInt(value.replace("%", ""));
                          if (onFieldChange && !isNaN(taxValue)) {
                            onFieldChange("inTax", taxValue, product.id);
                          }
                        }
                      }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Out Tax{" "}
                      <span style={{ color: theme.palette.custom.red.main }}>
                        *
                      </span>
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={["20%", "5%", "0%"]}
                      classes={{ root: classes.autoComplete }}
                      renderInput={(p) => (
                        <TextField {...p} variant="outlined" />
                      )}
                      value={localOutTax}
                      onChange={(e, value) => {
                        if (!isEditDisabled && value !== null) {
                          setLocalOutTax(value);
                          const taxValue = parseInt(value.replace("%", ""));
                          if (onFieldChange && !isNaN(taxValue)) {
                            onFieldChange("outTax", taxValue, product.id);
                          }
                        }
                      }}
                      disabled={isEditDisabled}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Hidden>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProductListItem;
